import { Suspense, lazy, useEffect, type ReactNode } from "react";
import { Navigate, createBrowserRouter, useLocation } from "react-router-dom";
import { AppShell } from "@/layouts/AppShell";
import { useSession } from "@/lib/store";
import { SplashScreen } from "@/screens/SplashScreen";
import LoginScreen from "@/screens/LoginScreen";
import SelectTenantScreen from "@/screens/SelectTenantScreen";
import ConversationsScreen from "@/screens/ConversationsScreen";

/**
 * Rotas secundarias entram por code-splitting (carregam sob demanda) para a
 * rota inicial — /conversas e /login — ficar com o bundle menor possivel.
 * O fallback e o mesmo splash, sem trocar o layout (zero CLS).
 */
const ChatScreen = lazy(() => import("@/screens/ChatScreen"));
const ContactDetailsScreen = lazy(() => import("@/screens/ContactDetailsScreen"));
const AcceptInviteScreen = lazy(() => import("@/screens/AcceptInviteScreen"));
const NotificationsScreen = lazy(() => import("@/screens/NotificationsScreen"));
const SearchScreen = lazy(() => import("@/screens/SearchScreen"));
const TasksScreen = lazy(() => import("@/screens/TasksScreen"));
const TeamScreen = lazy(() => import("@/screens/TeamScreen"));
const LabelsScreen = lazy(() => import("@/screens/LabelsScreen"));
const SettingsScreen = lazy(() => import("@/screens/SettingsScreen"));
const NotificationSettingsScreen = lazy(() => import("@/screens/NotificationSettingsScreen"));
const ChannelsScreen = lazy(() => import("@/screens/ChannelsScreen"));
const NewChannelScreen = lazy(() => import("@/screens/NewChannelScreen"));
const ReportsScreen = lazy(() => import("@/screens/ReportsScreen"));
const NotFoundScreen = lazy(() => import("@/screens/NotFoundScreen"));

function lazyRoute(node: ReactNode): ReactNode {
  return <Suspense fallback={<SplashScreen />}>{node}</Suspense>;
}

/** Resolve sessao + tenant ativo antes de montar o shell (RF-UX-002/008/012). */
function AuthGate() {
  const accessToken = useSession((s) => s.accessToken);
  const me = useSession((s) => s.me);
  const tenantId = useSession((s) => s.tenantId);
  const setTenant = useSession((s) => s.setTenant);
  const location = useLocation();

  useEffect(() => {
    if (me && !tenantId && me.tenants.length === 1) {
      const [only] = me.tenants;
      if (only) setTenant(only.id);
    }
  }, [me, tenantId, setTenant]);

  if (!accessToken || !me) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (me.tenants.length === 0) {
    return <Navigate to="/entrar/empresa" replace />;
  }
  if (!tenantId) {
    return me.tenants.length > 1 ? <Navigate to="/entrar/empresa" replace /> : <SplashScreen />;
  }
  return <AppShell />;
}

function LoginGate() {
  const accessToken = useSession((s) => s.accessToken);
  const me = useSession((s) => s.me);
  const tenantId = useSession((s) => s.tenantId);
  if (accessToken && me) {
    if (tenantId) return <Navigate to="/conversas" replace />;
    if (me.tenants.length === 0) return <Navigate to="/entrar/empresa" replace />;
  }
  return <LoginScreen />;
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginGate /> },
  { path: "/convite/:token", element: lazyRoute(<AcceptInviteScreen />) },
  { path: "/entrar/empresa", element: <SelectTenantScreen /> },
  {
    element: <AuthGate />,
    children: [
      { index: true, element: <Navigate to="/conversas" replace /> },
      { path: "conversas", element: <ConversationsScreen /> },
      { path: "c/:id", element: lazyRoute(<ChatScreen />) },
      { path: "contato/:id", element: lazyRoute(<ContactDetailsScreen />) },
      { path: "notificacoes", element: lazyRoute(<NotificationsScreen />) },
      { path: "busca", element: lazyRoute(<SearchScreen />) },
      { path: "tarefas", element: lazyRoute(<TasksScreen />) },
      { path: "equipe", element: lazyRoute(<TeamScreen />) },
      { path: "etiquetas", element: lazyRoute(<LabelsScreen />) },
      { path: "ajustes", element: lazyRoute(<SettingsScreen />) },
      { path: "ajustes/notificacoes", element: lazyRoute(<NotificationSettingsScreen />) },
      { path: "canais", element: lazyRoute(<ChannelsScreen />) },
      { path: "canais/novo", element: lazyRoute(<NewChannelScreen />) },
      { path: "relatorios", element: lazyRoute(<ReportsScreen />) },
      { path: "*", element: lazyRoute(<NotFoundScreen />) },
    ],
  },
]);
